<!--
This link contains lots of details concerning video recording - also note that it references taking screenshots, which may be useful if we ever intend to print things
https://www.html5rocks.com/en/tutorials/getusermedia/intro/
-->

<template>
  <div class="absolute-fill d-flex justify-center" style="overflow: hidden;">

    <div v-if="!playbackIsLoaded" style="z-index: 1;" class="absolute-fill placeholder" />

    <video
      v-show="localSrc || video.src"
      ref="playback"
      class="playback"
      style="z-index: 2;"
      v-bind:style="{ transform }"
      height="100%" playsinline
      v-bind="(!video.src || localSrc || isBlob) ? {} : { poster: video.src + '_poster_0' }"
      :src="localSrc || (video.src + (isBlob ? '' : '.mp4'))"
      @loadeddata="onLoadedData"
    />

    <video
      v-if="!localSrc && !video.src && isMediaRecorderSupported"
      ref="preview"
      class="playback"
      height="100%" autoplay playsinline muted
      v-bind:style="{ transform }"
      style="z-index: 2;"
    />

  </div>
</template>

<style scoped>
.placeholder {
  border: 4px dashed rgba(0, 0, 0, .1);
  border-radius: 8px;
}

.playback {
  /* These seem redundant, but they're necessary for proper cross-browser compatibility */
  height: 100%;
  min-height: 100%;
}
</style>

<script>
import { getMetadata } from '@/utils/video.js';
import { mdiUpload } from '@mdi/js';


// This looks to be the only codec common between all browsers
const MIME_TYPE = 'video/webm';


// These constraints define the size of the media stream tracks. Note that they are not all universally supported -
// the aspectRatio is a particular problem...
// Use this snippet to see what constraints are supported:
// console.log(navigator.mediaDevices.getSupportedConstraints());
// Note: we go slightly wider than 5x7 to make sure we don't have any white space on the sides (the excess is hidden)
const CONSTRAINTS = {
  width: { min: 360, ideal: 360 },
  height: { min: 480, ideal: 480 },
  advanced: [
    { width: 360, height: 480 },
    { aspectRatio: 0.75 }
  ]
  // width: 342,
  // height: 480,
  // aspectRatio: 0.714285
};


export default {
  name: 'video-blob',

  props: [ 'blob', 'isOwner', 'isCardOwner', 'isSelected', 'scale', 'isPageActive', 'mode' ],

  data() {
    return {
      localSrc: null,
      controls: new Promise(resolve => this.resolveControls = resolve),

      playback: null,
      playbackIsLoaded: false,

      mediaSource: null,
      recorder: null,

      count: null,
      countTimeout: null,

      isPreviewing: false,
      isRecording: false,
      isPaused: true,

      playhead: 0,
      duration: 0,
      updateDurationInterval: null,

      error: false,

      reloadStatus: this.$track('reload'),
      startCountdownStatus: this.$track('startCountdown'),
      didUpdateIsPreviewingStatus: this.$track('didUpdateIsPreviewing'),
    };
  },

  computed: {
    video() {
      return this.blob.video;
    },

    isBlob() {
      return this.video.src && this.video.src.startsWith('blob');
    },

    transform() {
      // The CSS transform to apply to the video element
      return this.video.mirror ? 'scale(-1, 1)' : null;
    },

    isMediaRecorderSupported() {
      return !!window.MediaRecorder && !IS_MOBILE;
    }
  },

  created() {
    Object.assign(this, { mdiUpload });
  },

  beforeDestroy() {
    this.closePreview();
  },

  methods: {
    async openPreview() {
      // Preconditions: the 'preview' and 'media-source-menu' nodes must exist in the DOM ('isPreviewing' and
      // 'isMediaRecorderSupported' must both be true)

      // Logically, this shouldn't be necessary, but I believe there's an issue with callback timing that necessitates
      // this. Defensively, I also want to make extra sure we don't have any lingering access to the camera.
      this.closePreview();

      if (!this.mediaSource) {
        // Selecting a media source will trigger the 'mediaSource' watcher to run this function again
        const controls = await this.controls;
        // I'm running into issues where when the record button is clicked for the first time, the 'media-source-menu'
        // does not exist right away, and no amount of calls to '$nextTick' seems to be able to resolve it
        // TODO: this is a terrible kludge - fix it!
        while (!controls.$refs['media-source-menu']) {
          await new Promise(r => setTimeout(r, 100));
        }
        await controls.$refs['media-source-menu'].selectDefault();
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia(this.mediaSource);
      await stream.getVideoTracks()[0].applyConstraints(CONSTRAINTS);

      this.recorder = new MediaRecorder(stream, { mimeType: MIME_TYPE });
      this.recorder.ondataavailable = e => this.recordedData.push(e.data);
      this.recorder.onstop = () => {
        // Setting the 'video.src' triggers the 'blob' to emit an 'input' event
        const blob = new Blob(this.recordedData, { type: MIME_TYPE });
        const url = URL.createObjectURL(blob)
        this.localSrc = url;
        this.video.src = url;
      };

      // Wait until the stream is ready (showing a preview)
      await new Promise(resolve => {
        const onLoadedData = e => {
          e.target.removeEventListener('loadeddata', onLoadedData);
          resolve();
        };
        this.$refs.preview.addEventListener('loadeddata', onLoadedData);
        this.$refs.preview.srcObject = stream;
      });
    },

    closePreview() {
      this.recorder && this.recorder.stream.getTracks().forEach(track => track.stop());
    },

    play() {
      this.isPaused = false;
      this.playback.play();
    },

    pause() {
      this.playback.pause();
      this.isPaused = true;
    },

    playPause() {
      if (this.isPaused)
        this.play();
      else
        this.pause();
    },

    seek(pct) {
      // This gets triggered initially when the value first gets set on the slider, hence the guard
      if (this.playback)
        this.playback.currentTime = (pct / 100.0) * this.duration;
    },

    async startCountdown() {
      this.pause();

      this.isPreviewing = true;
      this.isRecording = true;

      // Setting 'isPreviewing' will start the preview - we want to make sure that handler has actually finished
      // before we continue
      await this.$nextTick();
      await this.didUpdateIsPreviewingStatus.promise;

      if (!this.mediaSource) {
        this.isPreviewing = false;
        this.isRecording = false;
        return;
      }

      this.count = 3;
      this.countTimeout = setTimeout(this.decrementCountdown, 1000);
    },

    decrementCountdown() {
      this.count--;
      if (this.count)
        this.countTimeout = setTimeout(this.decrementCountdown, 1000);
      else
        // We hit 0, actually start the recording and make sure to clear any existing data
        this.startRecording();
    },

    startRecording() {
      this.recordedData = [];
      this.recorder.start();

      this.start = new Date();
      this.duration = 0;
      this.updateDurationInterval = setInterval(
        () => {
          this.duration = (new Date() - this.start) / 1000;
          this.$forceUpdate();
        },
        1000
      );

      // Prevent recording for more than a minute
      this.recordingTimeout = setTimeout(this.stopRecording, 60500);
    },

    stopRecording() {
      clearTimeout(this.recordingTimeout);
      this.isRecording = false;
      this.isPreviewing = !this.video.src;

      // It's possible to click the stop-recording button during the countdown
      if (this.count) {
        clearTimeout(this.countTimeout);
        this.count = null;
        return;
      }

      if (this.recorder && this.recorder.state == 'recording') {
        // The actual call to load the recorded data for playback is in recorder's 'onstop' callback
        this.recorder.stop();
        clearInterval(this.updateDurationInterval);
        this.duration = (new Date() - this.start) / 1000;
      }
    },

    // We have this declared separately from the watcher in order to await it
    async didUpdateIsPreviewing() {
      if (this.isPreviewing && this.isMediaRecorderSupported) {
        await this.$nextTick();
        await this.openPreview();
      } else {
        this.isRecording = false;
        this.closePreview();
      }
    },

    async reload() {
      await new Promise(r => setTimeout(r, 5000));
      this.playback.load();
      try {
        const metadata = await getMetadata(this.isBlob ? src : src + '.mp4');
        this.duration = metadata.duration;
        this.playbackIsLoaded = true;
        this.error = false;
      } catch (e) {
        this.error = true;
      }
    },

    async onLoadedData() {
      // This seems redundant, but it ensures that the video-blob-controls will correctly reflect the media state by
      // the time we emit the 'loaded' event
      this.error = false;
      this.playbackIsLoaded = true;
      await this.$nextTick();
      this.$emit('loaded');
    }
  },

  mounted() {
    this.playback = this.$refs.playback;

    // Chrome is terrible at computing the duration of new recordings. There are all sorts of workarounds, but the
    // simplest thing is just to keep track of the duration yourself. I was able to get the "fix" descibed in the SO
    // link to work for computing the duration, but that royally screwed up the simple play / pause functionality 
    // unless you seek somewhere in the video first). Chrome does seem to compute the exact duration as it plays the
    // video, which will vary by a tiny amount from what we computed. I defer to Chrome's value and update the slider
    // as the video plays, which is weird, but the impact on the user is impreceptible.
    // https://stackoverflow.com/questions/38443084/how-can-i-add-predefined-length-to-audio-recorded-from-mediarecorder-in-chrome/39971175#39971175

    // Set up the event listeners to update the playhead
    this.playback.addEventListener(
      'durationchange',
      () => {
        if (!isFinite(this.playback.duration))
          return;
        this.duration = this.playback.duration;
      }
    );
    this.playback.addEventListener('ended', () => { this.isPaused = true; this.seek(0); });
    this.playback.addEventListener('timeupdate', () => this.playhead = this.playback.currentTime);
  },

  beforeDestroy() {
    // Make sure the camera is off before we leave
    this.closePreview();
  },

  watch: {
    isPageActive(value) {
      if (!value)
        this.pause();
    },

    isSelected(value) {
      if (!value) {
        this.stopRecording();
        this.pause();
        if (!this.video.src)
          // The equivalent of a "Message here" blob for video
          this.$emit('delete');
      }
    },

    isPreviewing() {
      this.didUpdateIsPreviewing();
    },

    mediaSource() {
      if (this.isPreviewing) {
        if (this.mediaSource)
          this.openPreview();
        else
          this.isPreviewing = false;
      }
    },

    'video.src': {
      immediate: true,
      async handler(src, oldSrc) {
        this.error = false;

        // We might replace the 'video.src' externally when we save. In that case, we would be swapping out our local
        // 'blob' URL for an 'http' URL. We don't want to interrupt any active playback.
        if (this.localSrc)
          return;

        await this.$nextTick();
        this.pause();

        // We change the 'src' when we auto-save a video, but I don't want it to be invalidated if the old 'src' is
        // still there
        this.playbackIsLoaded = !!oldSrc;

        this.isPreviewing = !src;
        if (src) {
          // I was having issues in Safari, possible other browsers, getting the src to actually show - not going to
          // worry about it for now...
          this.playback.load();

          try {
            const metadata = await getMetadata(this.isBlob ? src : src + '.mp4');
            this.duration = metadata.duration;
            this.playbackIsLoaded = true;
            this.error = false;
          } catch (e) {
            this.error = true;
          }
        }
      }
    }
  }
}
</script>
